<template>
  <div class="employee-management-page" v-rect-loading="loading">
    <div class="basic-info">
      <div class="header">
        <div class="basic-info-title">{{ $t(`${i18nPrefix}.label.basicInfoTitle`) }}</div>
        <div class="container total-count">{{ $t(`${i18nPrefix}.label.allTotal`, {allTotal: allTotal}) }}</div>
        <div class="container">{{ $t(`${i18nPrefix}.label.pinCode`, {companyPinCode: companyPinCode}) }}</div>
        <div class="container email-suffix">
          <el-button @click="handleEmailSuffix">{{ $t(`${i18nPrefix}.label.setupEmailDomain`) }}</el-button>
        </div>
        <div class="container email-domain">
          <span class="title">{{ $t(`${i18nPrefix}.label.emailDomain`) }}</span>
          <span class="text-wrapper">
            <span class="text" v-for="(item, index) in emailSuffix" :key="index">{{ item }}</span>
          </span>
        </div>
        <div class="container company-employee-settings-container">
          <span class="text">{{ $t(`${i18nPrefix}.label.registerCheck`) }}</span>
          <el-switch v-model="checkRegisterSwitch" @change="onEnabledChange"/>
        </div>
      </div>
    </div>
    <TableList
      class="employee-management-list"
      row-key="id"
      :listData="employeeList"
      :title="$t(`${i18nPrefix}.title.employeeManagement`)"
      :tableColumns="tableColumns"
      :noAction="true"
      :showCreateBtn="false"
      :pagination="pagination"
      :defaultSort="defaultOrder"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
      <template #filter>
        <div class="header-left">
          <span class="text">{{ $t(`${i18nPrefix}.label.companyName`) }}</span>
          <span class="company-select">
            <el-select v-model="selectCompany" filterable clearable @change="handleSearch">
              <el-option
                v-for="item in companyList"
                :key="item.id"
                :label="item.i18nName"
                :style="{ paddingLeft: item.level * 15 + 'px' }"
                :value="item.id"
              >
              </el-option>
            </el-select>
          </span>
        </div>
        <div>
          <span class="employee-status-select" :class="currentEmployeeStatus===UNREGISTERED && 'unregistered'">
            <el-select v-model="employeeStatus" filterable clearable @change="handleSearch">
              <el-option
                v-for="(item, index) in employeeStatusList"
                :key="index"
                :label="item.selectedLabel"
                :value="item.value"
              >
              </el-option>
            </el-select>
          </span>
        </div>
        <div class="header-search">
          <el-input
            v-model="keywords"
            class="search-input"
            clearable
            :title="$t(`${i18nPrefix}.placeholder.search`)"
            :placeholder="$t(`${i18nPrefix}.placeholder.search`)"
            @input="handleKeywordsChange"
          >
          </el-input>
        </div>
      </template>
      <template #btn v-if="currentEmployeeStatus!=UNREGISTERED">
        <el-button v-if="currentEmployeeStatus!=RESIGNED" class="blue-btn" :disabled="!canClick" @click="handelResign">{{ $t(`${i18nPrefix}.btn.resign`) }}</el-button>
        <el-button v-if="currentEmployeeStatus!=REJECTED" class="blue-btn" :disabled="!canClick" @click="handelReject">{{ $t(`${i18nPrefix}.btn.reject`) }}</el-button>
        <el-button v-if="currentEmployeeStatus!=PENDING" class="blue-btn" :disabled="!canClick" @click="handlePending">{{ $t(`${i18nPrefix}.btn.pending`) }}</el-button>
        <el-button v-if="currentEmployeeStatus!=APPROVED" class="blue-btn" :disabled="!canClick" @click="handelApprove">{{ $t(`${i18nPrefix}.btn.approve`) }}</el-button>
        <el-button class="blue-btn" @click="handelBatchUpdateEmployeeStatus">{{ $t(`${i18nPrefix}.label.updateEmployeeStatus`) }}</el-button>
      </template>
      <template #operation="{ data, row }">
        <Dropdown
          :value="data"
          :data="filterOperationList(data)"
          @change="handleChangeEmployeeStatus($event, row)"
        />
      </template>
      <template #checkHeader>
        <el-checkbox :value="isCheckedAll" :indeterminate="indeterminate" @change="handleCheckAll"></el-checkbox>
      </template>
      <template #check="{ data, row }">
        <el-checkbox :value="data" @change="handleCheckOne($event, row)"></el-checkbox>
      </template>
      <template #disabled="{ data, row }">
        <el-switch :value="data" @change="onEnabledChange($event, row)"></el-switch>
      </template>
    </TableList>
    <DialogWrapper
      v-if="statusChangeDialog.show"
      class="status-change-dialog"
      saveBtnLabelI18nKey="common.btn.confirm"
      :closeOnClickModal="false"
      :title="statusChangeDialog.title"
      :loading="statusChangeDialog.loading"
      :show.sync="statusChangeDialog.show"
      @save="handleUpdateEmployeeStatus"
    />
    <DialogWrapper
      v-if="emailSuffixDialog.show"
      saveBtnLabelI18nKey="common.btn.confirm"
      class="email-suffix-dialog"
      :closeOnClickModal="false"
      :title="emailSuffixDialog.title"
      :loading="emailSuffixDialog.loading"
      :show.sync="emailSuffixDialog.show"
      @save="handleChangeEmailSuffix"
    >
    <div>
      <div class="email-suffix-content">{{ emailSuffixDialog.content }}</div>
      <el-input type="text" v-model="emailSuffixInfo"></el-input>
    </div>
    </DialogWrapper>
    <DialogWrapper
      v-if="batchChangeEmployeeDialog.show"
      class="batch-upload-employee-dialog"
      saveBtnLabelI18nKey="common.btn.confirm"
      :closeOnClickModal="false"
      :title="batchChangeEmployeeDialog.title"
      :loading="batchChangeEmployeeDialog.loading"
      :show.sync="batchChangeEmployeeDialog.show"
    >
      <div>
        <label>1. </label>
        <el-button type="text" class="template-link" href="" @click="downloadEmployee">{{ $t(`${i18nPrefix}.label.downloadEmployee`)  }}</el-button>
      </div>
      <div class="upload-input">
        <div class="file-type-tip"><label>2. {{ $t(`${i18nPrefix}.label.uploadEmployee`) }}</label></div>
        <el-input v-model="file.name" readonly="readonly" type="text" id="show_file" class="select-file-input" />
        <el-button class="select-file" size="small" @click="selectFile">{{ $t(`${i18nPrefix}.btn.upload`) }}</el-button>
      </div>
      <div>
        <span v-show="showUploadTip" class="file-error-tip">{{ $t(`${i18nPrefix}.message.uploadTip`) }}</span>
        <input type="file" ref="uploadFile" class="upload-file" @change="fileChange" />
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="upload-cancel" size="small" @click="handleClose">{{ $t(`common.btn.cancel`) }}</el-button>
        <el-button class="upload-submit" size="small" @click="handleBatchChangeEmployee">{{ $t(`common.btn.confirm`) }}</el-button>
      </span>
    </DialogWrapper>
    <DialogWrapper
      v-if="uploadResultDialog.show"
      class="upload-result-dialog"
      saveBtnLabelI18nKey="common.btn.confirm"
      :show.sync="uploadResultDialog.show"
      :closeOnClickModal="false"
    >
      <div class="result-info">
        <div class="title">{{ $t(`${i18nPrefix}.label.batchUploadResult`) }}</div>
        <div>{{ $t(`${i18nPrefix}.label.uploadCount`, {total: uploadResultData.upload_count}) }}</div>
        <div>{{ $t(`${i18nPrefix}.label.updateCount`, {total: uploadResultData.update_count}) }}</div>
        <div>{{ $t(`${i18nPrefix}.label.waitRegisterCount`, {total: uploadResultData.register_count}) }}</div>
        <div class="fail">{{ $t(`${i18nPrefix}.label.failCount`, {total: taskResult.length}) }}</div>
        <a v-if="taskResult?.length" @click="handleShowResult">{{ $t(`${i18nPrefix}.label.detail`) }}</a>
        <div v-if="taskResult?.length && showResult" class="content">
          <div v-for="(item, index) in taskResult" :key="index">
            <div>{{ $t(`${i18nPrefix}.label.rows`, {row: item.row_num}) }} {{ $t(`${i18nPrefix}.label.${item.type}`, {status: item.status_str, code: item.employee_code, email: item.email}) }}</div>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button class="upload-submit" size="small" @click="confirmResult">{{ $t(`common.btn.confirm`) }}</el-button>
      </span>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import Dropdown from '@/components/formEditor/dropdown.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

const All = '0'
const PENDING = '1'
const APPROVED = '2'
const REJECTED = '3'
const RESIGNED = '4'
const UNREGISTERED = '5'

const STATUS_MAP = {
  [PENDING]: 'pending',
  [APPROVED]: 'approved',
  [REJECTED]: 'rejected',
  [RESIGNED]: 'resigned',
  [UNREGISTERED]: 'unregistered'
}

export default {
  components: {
    TableList,
    Dropdown,
    DialogWrapper
  },
  data () {
    return {
      i18nPrefix: 'admin.company.employeeManagement',
      PENDING: PENDING,
      APPROVED: APPROVED,
      REJECTED: REJECTED,
      RESIGNED: RESIGNED,
      UNREGISTERED: UNREGISTERED,
      STATUS_MAP: STATUS_MAP,
      loading: false,
      isEn,
      employeeList: [],
      companyList: [],
      wrappedFilterList: [],
      file: {},
      fileType: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'application/wps-office.xlsx'],
      showUploadTip: false,
      taskResult: [],
      uploadResultData: {},
      showResult: false,
      selectCompany: null,
      currentCompanyId: null,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      keywords: null,
      employeeStatus: '1',
      defaultEmployeeStatus: '0',
      order: '-updated_time',
      isCheckedAll: false,
      indeterminate: false,
      checkRegisterSwitch: false,
      emailSuffix: [],
      companyPinCode: null,
      rootCompanyId: null,
      statusChangeDialog: {
        show: false,
        loading: false,
        title: null,
        content: null,
        row: null,
        status: null
      },
      emailSuffixDialog: {
        show: false,
        loading: false,
        title: null,
        content: null
      },
      batchChangeEmployeeDialog: {
        show: false,
        loading: false,
        title: null,
        content: null
      },
      uploadResultDialog: {
        show: false,
        loading: false,
        title: null,
        content: null
      },
      allTotal: 0,
      emailSuffixInfo: null
    }
  },
  computed: {
    currentEmployeeStatus () {
      return this.employeeStatus || this.defaultEmployeeStatus
    },
    operationList () {
      const operationList = [
        {
          label: this.$t(`${this.i18nPrefix}.label.pending`),
          selectedLabel: this.$t(`${this.i18nPrefix}.label.pending`),
          value: PENDING
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.approve`),
          selectedLabel: this.$t(`${this.i18nPrefix}.label.approved`),
          value: APPROVED
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.reject`),
          selectedLabel: this.$t(`${this.i18nPrefix}.label.rejected`),
          value: REJECTED
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.resign`),
          selectedLabel: this.$t(`${this.i18nPrefix}.label.resigned`),
          value: RESIGNED
        },
        {
          selectedLabel: this.$t(`${this.i18nPrefix}.label.unregistered`),
          value: UNREGISTERED
        }
      ]
      return operationList
    },
    employeeStatusList () {
      const employeeStatusList = _.cloneDeep(this.operationList)
      employeeStatusList.push({
        label: this.$t(`${this.i18nPrefix}.label.statusAll`),
        selectedLabel: this.$t(`${this.i18nPrefix}.label.statusAll`),
        value: All
      })
      return employeeStatusList
    },
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.checkAll`),
          dataKey: 'isChecked',
          slotName: 'check',
          headerSlotName: 'checkHeader',
          width: '20px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.email`),
          dataKey: 'email_address',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.validationInfo`),
          dataKey: 'auth_info',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.name`),
          dataKey: 'full_name',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.updateTime`),
          dataKey: 'updated_time',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          formatter (data) {
            return moment(data).format('YYYY-MM-DD')
          },
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.operation`),
          dataKey: 'status',
          slotName: 'operation'
        }
      ]
      return columns
    },
    defaultOrder () {
      const order = this.order
      if (order.includes('-')) {
        return {
          prop: order.split('-')[1],
          order: 'descending'
        }
      } else {
        return {
          prop: order,
          order: 'ascending'
        }
      }
    },
    filterList () {
      const list = [
        {
          filterList: {
            company: {
              elType: 'el-select',
              optionLabel: 'i18nName',
              optionValue: 'id',
              optionSearch: true,
              optionList: this.companyList,
              style: (company) => {
                return { 'padding-left': `${(company.level || 1) * 15}px` }
              },
              class: 'company-select',
              loader: this.loadCompanyList
            }
          }
        }
      ]
      return list
    },
    totalStatusTitle () {
      return this.$t(`${this.i18nPrefix}.message.employeeTip`, { activeCount: this.activeCount, totalCount: this.totalCount })
    },
    canClick () {
      let canClick = false
      _.forEach(this.employeeList, employee => {
        if (employee.isChecked) {
          canClick = true
          return false
        }
      })
      return canClick
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('employeeManagement', [
      'getEmployeeList',
      'updateCompanyRegisterStatus',
      'updateEmployeeStatus',
      'updateEmailSuffix',
      'uploadEmployee',
      'getUploadEmployeeResult'
    ]),
    ...mapActions('company', ['getHRAccessibleCompanyTree']),
    async initData () {
      this.loading = true
      try {
        await this.loadCompanyList()
        await this.loadEmployeeList()
      } finally {
        this.loading = false
      }
    },
    async loadCompanyList () {
      const companyTreeList = await this.getHRAccessibleCompanyTree()
      const currentCompanyTree = companyTreeList[0]
      const currentCompany = currentCompanyTree.self
      this.selectCompany = currentCompany.id
      this.currentCompanyId = currentCompany.id
      const companyList = []
      _.forEach(companyTreeList, companyTree => {
        this.formatCompanyData(companyTree, companyTree.self, companyList)
      })
      this.companyList = companyList
    },
    async loadEmployeeList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      try {
        this.loading = true
        const params = {
          company_id: this.selectCompany || undefined,
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          keywords: this.keywords,
          employee_status: this.currentEmployeeStatus,
          order: this.order
        }
        const resp = await this.getEmployeeList(params)
        this.employeeList = _.map(resp.employee_list.employee_list_info, employee => {
          employee.isChecked = false
          return employee
        })
        this.indeterminate = false
        this.isCheckedAll = false
        this.emailSuffix = resp.email_suffix
        this.companyPinCode = resp.company_pin_code
        this.checkRegisterSwitch = resp.validate_employee_register
        this.allTotal = resp.all_total
        this.pagination.total = resp.employee_list.total_count
      } finally {
        this.loading = false
      }
    },
    filterOperationList (data) {
      let operationList = _.cloneDeep(this.operationList)
      if (data !== UNREGISTERED) {
        operationList = _.chain(operationList).filter(item => {
          return item.value !== UNREGISTERED
        }).forEach(item => {
          if (item.value === data) {
            item.disabled = true
          }
        }).value()
      } else {
        operationList = _.chain(operationList).filter(item => {
          return item.value === UNREGISTERED || item.value === RESIGNED
        }).forEach(item => {
          if (item.value === data) {
            item.disabled = true
          }
        }).value()
      }
      return operationList
    },
    handleChangeEmployeeStatus (status, row) {
      this.changeStatusDialogData(false, status, row)
    },
    handelResign () {
      this.changeStatusDialogData(false, RESIGNED)
    },
    handelReject () {
      this.changeStatusDialogData(false, REJECTED)
    },
    handlePending () {
      this.changeStatusDialogData(false, PENDING)
    },
    handelApprove () {
      this.changeStatusDialogData(false, APPROVED)
    },
    async handleUpdateEmployeeStatus () {
      const status = this.statusChangeDialog.status
      const row = this.statusChangeDialog.row

      this.statusChangeDialog.loading = true
      const selectedAccountIds = []
      if (row) {
        selectedAccountIds.push(row.id)
      } else {
        _.forEach(this.employeeList, employee => {
          if (employee.isChecked) {
            selectedAccountIds.push(employee.id)
          }
        })
      }
      try {
        const data = {
          account_ids: selectedAccountIds,
          to_status: status
        }
        const resp = await this.updateEmployeeStatus(data)
        if (resp.success) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          if (row) {
            row.status = status
          } else {
            this.loadEmployeeList(true)
          }
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.changeStatusDialogData(true)
      }
    },
    changeStatusDialogData (init, status, row) {
      if (init) {
        this.statusChangeDialog.loading = false
        this.statusChangeDialog.show = false
        this.statusChangeDialog.row = null
      } else {
        const statusTip = this.$t(`${this.i18nPrefix}.label.${STATUS_MAP[status]}`)
        const title = this.$t(`${this.i18nPrefix}.label.employeeStatusTip`, { status: statusTip })
        this.statusChangeDialog.title = title
        this.statusChangeDialog.status = status
        this.statusChangeDialog.row = row
        this.statusChangeDialog.show = true
      }
    },
    handelBatchUpdateEmployeeStatus () {
      this.batchChangeEmployeeDialog.show = true
    },
    handleCheckAll (value) {
      this.isCheckedAll = value
      if (value) {
        _.forEach(this.employeeList, item => {
          item.isChecked = true
        })
      } else {
        _.forEach(this.employeeList, item => {
          item.isChecked = false
        })
      }
      this.indeterminate = false
    },
    handleCheckOne (value, row) {
      row.isChecked = value
      let count = 0
      _.forEach(this.employeeList, item => {
        if (item.isChecked) {
          count++
        }
      })
      if (count > 0 && count < this.employeeList.length) {
        this.indeterminate = true
      } else {
        this.indeterminate = false
      }
      if (count === this.employeeList.length) {
        this.isCheckedAll = true
      } else {
        this.isCheckedAll = false
      }
    },
    handlePaginationChange () {
      this.loadEmployeeList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.loadEmployeeList()
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.handleSearch, 1000)
      }
      this.keywordsSearchDebounce()
    },
    handleSearch () {
      this.loadEmployeeList(true)
    },
    async onEnabledChange (status) {
      this.loading = true
      try {
        const data = {
          company_id: this.selectCompany || this.currentCompanyId,
          enable: status
        }
        const resp = await this.updateCompanyRegisterStatus(data)
        if (resp?.data?.success) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        } else {
          this.checkRegisterSwitch = !this.checkRegisterSwitch
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.loading = false
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        _.forEach(children, (child) => {
          this.formatCompanyData(data, child, companyList)
        })
      }
      return companyList
    },
    downloadEmployee () {
      window.location.href = this.$DjangoBaseUrl('/referral/download-company-employee/')
    },
    uploadSuccess () {
      this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
      this.batchChangeEmployeeDialog.loading = false
      this.batchChangeEmployeeDialog.show = false
    },
    uploadError () {
      this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      this.batchChangeEmployeeDialog.loading = false
    },
    handleClose () {
      this.showUploadTip = false
      this.batchChangeEmployeeDialog.show = false
      this.clearFile()
    },
    async handleBatchChangeEmployee () {
      if (!this.handleBeforeUpload(this.file)) {
        return
      }
      this.batchChangeEmployeeDialog.loading = true
      try {
        const resp = await this.uploadEmployee(this.file)
        const taskId = resp.data?.task_id
        if (taskId) {
          let resultStatus = false
          while (!resultStatus) {
            const resp = await this.getUploadEmployeeResult({ task_id: taskId })
            const respData = resp.data
            const status = respData?.status
            if (respData && status) {
              if (status === 'finished') {
                resultStatus = true
                const result = respData.result
                this.uploadResultData = result
                _.forEach(result.has_used, item => {
                  item.type = 'hasUsed'
                })
                _.forEach(result.invalid_status, item => {
                  item.type = 'invalidStatus'
                })
                _.forEach(result.no_email, item => {
                  item.type = 'noEmail'
                })
                _.forEach(result.no_register, item => {
                  item.type = 'noRegister'
                })
                _.forEach(result.not_found, item => {
                  item.type = 'notFound'
                })
                _.forEach(result.duplicate_email, item => {
                  item.type = 'duplicateEmail'
                })
                this.taskResult = _.chain(_.merge({}, result.has_used, result.invalid_status, result.no_email, result.no_register, result.not_found))
                  .values()
                  .flatMap()
                  .sortBy('row_num')
                  .value()
              } else if (status === 'error') {
                this.uploadError()
                return
              } else {
                await new Promise(resolve => setTimeout(resolve, 1000))
              }
            } else {
              this.uploadError()
            }
          }
          this.uploadSuccess()
          this.uploadResultDialog.show = true
        } else {
          this.uploadError()
        }
      } finally {
        this.clearFile()
        this.batchChangeEmployeeDialog.loading = false
      }
    },
    handleShowResult () {
      this.showResult = !this.showResult
    },
    confirmResult () {
      this.uploadResultDialog.show = false
      this.loadEmployeeList()
    },
    handleBeforeUpload (file) {
      if (!this.fileType.includes(file.type)) {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.uploadType`))
        return false
      }
      return true
    },
    handleUploadError (resp, file) {
      this.$message({
        message: this.$t(`${this.i18nPrefix}.message.uploadError`),
        duration: 2000,
        type: 'warning'
      })
    },
    clearFile () {
      this.$refs.uploadFile.value = ''
      this.file = {
        lastModified: null,
        lastModifiedDate: null,
        name: '',
        size: null,
        type: '',
        webkitRelativePath: ''
      }
    },
    selectFile () {
      this.clearFile()
      this.$refs.uploadFile.dispatchEvent(new MouseEvent('click'))
      this.showUploadTip = false
    },
    fileChange () {
      this.file = this.$refs.uploadFile.files[0]
      if (!this.file) {
        this.file = {
          lastModified: null,
          lastModifiedDate: null,
          name: '',
          size: null,
          type: '',
          webkitRelativePath: ''
        }
      }
    },
    handleEmailSuffix () {
      this.emailSuffixDialog.show = true
      this.emailSuffixDialog.title = this.$t(`${this.i18nPrefix}.label.emailSuffixTitle`)
      this.emailSuffixDialog.content = this.$t(`${this.i18nPrefix}.label.emailSuffixContent`)
      this.emailSuffixInfo = this.emailSuffix.join('|')
    },
    async handleChangeEmailSuffix () {
      const regex = /^[a-zA-Z0-9.\-|]+$/
      if (!regex.test(this.emailSuffixInfo)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.emailSuffixValidate`))
        return
      }
      this.emailSuffixDialog.loading = true
      try {
        const params = {
          email_suffix: this.emailSuffixInfo,
          current_company_id: this.selectCompany || this.currentCompanyId
        }
        const resp = await this.updateEmailSuffix(params)
        if (resp.success) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
          this.loadEmployeeList()
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
        }
      } finally {
        this.emailSuffixDialog.loading = false
        this.emailSuffixDialog.show = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.employee-management-page {
  .basic-info {

    .header {
      margin: 5px 0;

      .basic-info-title {
        font-size: 18px;
        color: #303133;
        font-weight: 500;
        margin-bottom: 20px;
      }

      .company-employee-settings-container {
        display: inline-block;
        vertical-align: middle;

        .text {
          color: #606266;
          margin-right: 10px;
          vertical-align: middle;
        }
      }

      .email-domain {
        display: flex;

        .title {
          flex-shrink: 0;
          white-space: pre-wrap;
        }

        .text-wrapper {
          display: flex;
          flex-wrap: wrap;

          .text {
            color: #606266;
            margin-right: 10px;
            vertical-align: middle;
          }
        }
      }

      .container {
        margin-bottom: 15px;
        color: #606266;
        margin-right: 10px;
        vertical-align: middle;
      }
    }

    ::v-deep {
      .el-button {
        padding: 6px 14px;
        border-radius: 2px;
        border-color: #0096cc;
        color: #0096cc;
        line-height: 18px;
      }
    }
  }

  .employee-management-head {
    margin-bottom: 10px;

    .connect-body {
      background-color: #f9f9f9;
      border: 1px solid #ccc;
      padding: 10px 10px;
      margin-top: 10px;

      .text {
        font-size: 12px;
        color: #303133;
      }
    }

    ::v-deep {
      .el-button {
        padding: 6px 14px;
        border-radius: 2px;
        border-color: #0096cc;
        color: #0096cc;
        line-height: 18px;
      }
    }
  }
  .employee-management-list {

    .statistics {
      display: inline-block;

      .success-icon {
        font-size: 20px;
        color: #0096cc;
        vertical-align: middle;
        margin-right: 5px;
      }

      .error-icon {
        font-size: 17px;
        color: #aaa;
        vertical-align: middle;
        margin-right: 5px;
      }

      .statistics-count {
        border-right: 1px solid #aaa;
        margin-right: 10px;
        padding-right: 10px;
        vertical-align: middle;
      }
    }

    .header-left {
      display: inline-block;
      vertical-align: middle;
      .text {
        margin-right: 10px;
        display: inline-block;
        vertical-align: middle;
      }

      .company-select {
        display: inline-block;
        vertical-align: middle;
      }
    }
    .employee-status-select {

      ::v-deep .el-input__inner{
        width: 120px;
      }
    }

    .unregistered {

      ::v-deep .el-input__inner{
        width: 150px;
      }
    }

    .header-search {

      ::v-deep {

        .el-input__inner {
          border: 1px solid #c0c4cc;
        }

        .el-input-group__append {
          border-left: none;
          padding: 0 5px;
          background-color: #fff;
        }
      }

      .icon-search {
        cursor: pointer;
        font-size: 18px;
      }
    }

    ::v-deep {

      .list-head {
        height: auto;
        flex-wrap: wrap;
        justify-content: flex-end;
        margin-bottom: 10px;

        .title {
          margin-bottom: 10px;
          flex-shrink: 0;
        }

        .filter-container {
          margin-bottom: 10px;
          flex-shrink: 0;
        }

        .btn-container {
          margin-bottom: 10px;
          word-break: keep-all;
          white-space: nowrap;
        }

      }

      .el-checkbox__label {
        color: #909699;
      }

    }
  }

  .download-employee-container {
    margin-bottom: 10px;

    .download-employee {
      color: #0096cc;
      cursor: pointer;
      &:hover {
        color: #0096cc;
      }
    }
  }

  .email-suffix-content {
    margin-bottom: 10px;
  }

  .result-info {

    .title {
      font-size: 18px;
      margin-bottom: 5px;
    }

    .fail {
      display: inline-block;
      margin-right: 5px
    }

    .content {
      background: #f9f9f9;
      border: 1px solid #ccc;
      padding: 5px 0 5px 10px;
      margin-top: 5px;
    }

    a {
      color: #0096cc;
      cursor: pointer;
    }
  }

  .upload-input {
    display: flex;
    align-items: center;

    .select-file-input {
      flex: 1;
      margin-left: 10px;
    }
  }

  .upload-cancel {
    background-color: #aaa;
    color:#ffffff;
    width: 86px;
    margin-right: 30px;
    border-width: 0;
    font-weight: 700;
  }

  .upload-submit {
    width: 86px;
    background-color: #0096cc;
    color: #fff;
    border-width: 0;
    font-weight: 700;
  }

  .select-file {
    margin-left: 5px;
    background-color: #aaa;
    color: #ffffff;
    width: 86px;
    border-width: 0;
    font-weight: 700;
  }

  .upload-file {
    display: none;
  }

  .file-error-tip {
    margin-left: 10px;
    color: red;
  }

  .email-suffix-dialog {
    ::v-deep {
      .el-dialog {
        width: 700px;
      }
    }
  }

  .status-change-dialog {
    ::v-deep {
      .el-dialog {
        width: 590px;
      }
    }
  }

  .batch-upload-employee-dialog {
    ::v-deep {
      .el-dialog {
        width: 590px;
      }
    }
  }

  .upload-result-dialog {
    ::v-deep {
      .el-dialog {
        width: 590px;
      }
    }
  }
}

</style>
